/**
 * visitTracker.js
 * 방문자 추적 유틸리티
 *
 * 기능:
 *  - sessionStorage에 UUID 세션 ID 생성/관리
 *  - 세션 시작 API 호출 (referrer, landing URL)
 *  - 페이지 이동 시 page API 호출 (Vue Router afterEach에서 호출)
 *  - 30초 heartbeat ping (체류시간 갱신)
 *  - visibilitychange / beforeunload 시 end API 호출 (sendBeacon)
 */

const SESSION_KEY  = 'vt_session_id';
const SESSION_START = 'vt_session_start';
const BASE_URL     = '/api/v1/visit';
const PING_INTERVAL_MS = 30000; // 30초
const SHOP_ID = 6; // 기본 shop_id

let _sessionId    = null;
let _pingTimer    = null;
let _startTime    = null;   // 세션 전체 시작 시각 (ms)
let _pageTime     = null;   // 현재 페이지 시작 시각 (ms)
let _initialized  = false;
let _isBot        = false;
let _pageOrder    = 0;      // 세션 내 페이지 방문 순서 카운터
let _prevPageUrl  = null;   // 직전 페이지 URL (from_url용)
let _pageStartTime = null;  // 현재 페이지 진입 시각 (ms)

// ─── UUID 생성 ───────────────────────────────────────────────
function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// ─── 봇 감지 ─────────────────────────────────────────────────
function detectBot() {
    const ua = navigator.userAgent?.toLowerCase() || '';
    const bots = ['bot', 'crawl', 'spider', 'slurp', 'baidu', 'yandex', 'facebookexternalhit'];
    return bots.some((b) => ua.includes(b));
}

// ─── UTM 파라미터 추출 ────────────────────────────────────────
function getUtmSource() {
    const params = new URLSearchParams(window.location.search);
    return params.get('utm_source') || null;
}

// ─── 체류시간 계산 (초) ───────────────────────────────────────
function getSessionDuration() {
    if (!_startTime) return 0;
    return Math.floor((Date.now() - _startTime) / 1000);
}

// ─── API 호출 (fetch) ─────────────────────────────────────────
async function apiPost(endpoint, body) {
    try {
        await fetch(`${BASE_URL}/${endpoint}`, {
            method:  'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify(body),
            keepalive: true,
        });
    } catch (_) {
        // 추적 실패는 조용히 무시
    }
}

// ─── sendBeacon (탭 닫힘 등 비동기 안전) ─────────────────────
function apiBeacon(endpoint, body) {
    const url = `${BASE_URL}/${endpoint}`;
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    if (navigator.sendBeacon) {
        navigator.sendBeacon(url, blob);
    }
}

// ─── 세션 시작 ────────────────────────────────────────────────
async function startSession() {
    const referrer   = document.referrer || null;
    const landingUrl = window.location.pathname + window.location.search;
    const utmSource  = getUtmSource();

    // User-Agent 기반 정보 (간단히 OS/브라우저 감지)
    const ua     = navigator.userAgent;
    const mobile = /Mobi|Android|iPhone|iPad/i.test(ua) ? 'Y' : null;

    await apiPost('start', {
        shop_id:     SHOP_ID,
        session_id:  _sessionId,
        referer:     referrer,
        utm_source:  utmSource,
        landing_url: landingUrl,
        mobile:      mobile,
        user_agent:  ua,
    });
}

// ─── 페이지 이동 기록 ─────────────────────────────────────────
async function recordPage(pageUrl, fromUrl, pageDuration, pageOrder) {
    await apiPost('page', {
        shop_id:       SHOP_ID,
        session_id:    _sessionId,
        page_url:      pageUrl,
        from_url:      fromUrl,
        page_duration: pageDuration,
        page_order:    pageOrder,
    });
}

// ─── Heartbeat ping ───────────────────────────────────────────
function startPing() {
    stopPing();
    _pingTimer = setInterval(() => {
        if (_sessionId) {
            apiPost('ping', {
                session_id: _sessionId,
                duration:   getSessionDuration(),
            });
        }
    }, PING_INTERVAL_MS);
}

function stopPing() {
    if (_pingTimer) {
        clearInterval(_pingTimer);
        _pingTimer = null;
    }
}

// ─── 세션 종료 ────────────────────────────────────────────────
function endSession() {
    if (!_sessionId) return;
    stopPing();
    apiBeacon('end', {
        session_id: _sessionId,
        duration:   getSessionDuration(),
    });
}

// ─── 공개 API ─────────────────────────────────────────────────

/**
 * 앱 최초 로드 시 1회 초기화
 * app.js 또는 init.js에서 호출
 */
async function init() {
    if (_initialized) return;
    _initialized = true;

    // 봇이면 추적 중단
    _isBot = detectBot();
    if (_isBot) return;

    // 세션 ID 관리 (sessionStorage: 탭 단위)
    _sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!_sessionId) {
        _sessionId = generateUUID();
        sessionStorage.setItem(SESSION_KEY, _sessionId);
        _startTime = Date.now();
        sessionStorage.setItem(SESSION_START, String(_startTime));
        // 랜딩 페이지: PAGE_ORDER=1
        _pageOrder    = 1;
        _prevPageUrl  = null;
        _pageStartTime = Date.now();
        await startSession();
    } else {
        // 기존 세션 복원
        const saved = sessionStorage.getItem(SESSION_START);
        _startTime = saved ? parseInt(saved) : Date.now();
        _pageOrder = 1;
        _prevPageUrl  = null;
        _pageStartTime = Date.now();
    }

    _pageTime = Date.now();
    startPing();

    // 탭 가시성 변경 (탭 전환, 최소화)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            endSession();
        }
    });

    // 탭/브라우저 닫힘
    window.addEventListener('beforeunload', () => {
        endSession();
    });
}

/**
 * Vue Router afterEach에서 호출
 * 페이지 변경마다 기록
 */
function onPageChange(to, from) {
    if (!_sessionId || _isBot) return;
    if (!from || from.name === null) return; // 최초 진입은 startSession에서 처리

    // 1. 이전 페이지 체류시간 계산 (초)
    const pageDuration = _pageStartTime
        ? Math.floor((Date.now() - _pageStartTime) / 1000)
        : 0;

    // 2. 직전 페이지 URL (from_url)
    const fromUrl = _prevPageUrl || (from.fullPath ?? null);

    // 3. 페이지 순서 증가
    _pageOrder++;

    const pageUrl = to.fullPath || to.path;
    recordPage(pageUrl, fromUrl, pageDuration, _pageOrder);

    // 4. 현재 페이지 기록 갱신
    _prevPageUrl   = pageUrl;
    _pageStartTime = Date.now();
    _pageTime = Date.now();
}

/**
 * 회원가입 완료 시 호출 (AuthController 회원가입 성공 후 프론트에서 호출)
 */
async function markRegistered() {
    if (!_sessionId) return;
    await apiPost('ping', {
        session_id: _sessionId,
        duration:   getSessionDuration(),
        registered: 1,
    });
}

export default {
    init,
    onPageChange,
    markRegistered,
};
